import { createListenerMiddleware, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { dataWranglerApiSlice } from "../../api/dataWranglerApiSlice";
import { selectParticipantLimit } from "./dataWranglerSlice";
import { selectSelectedStudy } from '../common/databaseSlice';
import { transfer } from "@globus/sdk";
import { previousReleaseApiSlice } from "../../api/previousReleaseApiSlice";
import { isReauthRequiredError, LS_ERROR } from "../../hooks/useGlobusTransfer";

export type UserCollection = {
    id: string;
    label: string;
    directory?: string;
}

const initialState = {
    userCollection: {
        id: null,
        label: null,
        directory: "/{server_default}/",
    },
    preparingGlobusTransfer: {
        status: false,
        complete: false,
        page: 0,
        pageCount: 0,
        error: false,
        errorMessage: ''
    }
}

const globusSlice = createSlice({
    name: 'globus',
    initialState: initialState as any,
    reducers: {
        setUserCollection: (state, action: PayloadAction<UserCollection>) => {
            state.userCollection.id = action.payload.id;
            state.userCollection.label = action.payload.label;
            state.userCollection.directory = action.payload.directory || initialState.userCollection.directory;
        },
        resetUserCollection: (state) => {
            state.userCollection = initialState.userCollection;
        },
        setUserCollectionDirectory: (state, action: PayloadAction<string>) => {
            state.userCollection.directory = action.payload;
        },
        globusTransferFiles: (state, action: PayloadAction<any>) => {
            state.preparingGlobusTransfer = {
                status: true,
                complete: false,
                page: action.payload.globalPage,
                pageCount: action.payload.pageCount,
                error: false,
                errorMessage: ''
            }
        },

        globusTransferPreviousFiles: (state, action: PayloadAction<any>) => {
            state.preparingGlobusTransfer = {
                status: true,
                complete: false,
                page: action.payload.globalPage,
                pageCount: action.payload.pageCount,
                error: false,
                errorMessage: ''
            }
        },
        globusTransferFilesComplete: (state) => {
            state.preparingGlobusTransfer = {
                status: true,
                complete: true,
                page: state.preparingGlobusTransfer.page + 1,
                pageCount: state.preparingGlobusTransfer.pageCount,
                error: false,
                errorMessage: ''
            }
        },
        globusTransferFilesError: (state, action: PayloadAction<string>) => {
            state.preparingGlobusTransfer = {
                status: false,
                complete: false,
                page: 0,
                pageCount: 0,
                error: true,
                errorMessage: action.payload
            }
        },
        globusTransferFilesReset: (state) => {
            state.preparingGlobusTransfer = initialState.preparingGlobusTransfer;
        },
    }
});

export const {
    setUserCollection,
    resetUserCollection,
    setUserCollectionDirectory,
    globusTransferFiles,
    globusTransferPreviousFiles,
    globusTransferFilesComplete,
    globusTransferFilesError,
    globusTransferFilesReset,
} = globusSlice.actions;

export default globusSlice.reducer;

export const selectGlobusTransferStatus = (state: any) => state.globus.preparingGlobusTransfer;
export const selectUserCollection = (state: any) => state.globus.userCollection;

const globusListenerMiddleware = createListenerMiddleware();

const getSourceGlobusCollection = (sourceName: string, study: string) => {
    const globusCollections = window.env[`globusCollections`];

    return (
        sourceName == 'current-release' ?
            globusCollections[study] :
            (sourceName == 'previous-releases' ?
                globusCollections[`${study}Previous`] :
                globusCollections[sourceName]
            )
    );
}

globusListenerMiddleware.startListening({
    actionCreator: globusTransferFiles,
    effect: async (action, listenerApi) => {
        const payload = action.payload;
        const participantLimit = selectParticipantLimit(listenerApi.getState());
        const globusFileSummary = localStorage.getItem("globus-file-summary");
        const { data: files } = await listenerApi.dispatch(dataWranglerApiSlice.endpoints
            .adwTransferFiles.initiate({
                summary: globusFileSummary,
                selected: {
                    query: payload.fileSelection,
                    participants: payload.participantFilter
                },
                type: "",
                page: payload.sourcePage,
                source: payload.sources[payload.sourceIndex],
                limit: window.env.globusLimit,
                participantLimit: Number.isInteger(participantLimit) ? participantLimit : null,
            }));

        const submissionResponse = await transfer.taskSubmission.submissionId(
            {}, { manager: payload.authorization });
        const deadline = new Date();
        deadline.setDate(deadline.getDate() + 30)
        deadline.setHours(deadline.getHours() - 1)
        const submission = await submissionResponse.json();

        const selectedStudy = selectSelectedStudy(listenerApi.getState());
        const sourceName = payload.sources[payload.sourceIndex];
        const sourceGlobusCollection = getSourceGlobusCollection(sourceName, selectedStudy);

        const transferPayload: any = {
            submission_id: submission.value,
            source_endpoint: sourceGlobusCollection,
            destination_endpoint: payload.collection,
            deadline: deadline.toISOString(),
            label: `Lasso Informatics ${selectedStudy} File Transfer (${payload.globalPage + 1} of ${payload.pageCount})`,
            sync_level: 1,
            DATA: []
        }

        files?.forEach((file: any) => {
            transferPayload['DATA'].push({
                DATA_TYPE:"transfer_item",
                source_path: `/${file['prefix']}${file['filename']}`,
                destination_path: `${payload.path}/${file['filename']}`
            });
        });

        const transferPromise = await transfer.taskSubmission.submitTransfer({
            payload: transferPayload
        }, { manager: payload.authorization });
        const transferResponse = await transferPromise.json();

        if (['Accepted', 'Duplicate'].includes(transferResponse.code)) {
            if (files.length >= window.env.globusLimit) {
                listenerApi.dispatch(globusTransferFiles({
                    ...payload,
                    globalPage: payload.globalPage + 1,
                    sourcePage: payload.sourcePage + 1
                }))
            } else if (payload.sourceIndex < payload.sources.length - 1) {
                listenerApi.dispatch(globusTransferFiles({
                    ...payload,
                    globalPage: payload.globalPage + 1,
                    sourcePage: 0,
                    sourceIndex: payload.sourceIndex + 1
                }));
            } else {
                listenerApi.dispatch(globusTransferFilesComplete());
            }
        } else if (isReauthRequiredError(transferResponse)) {
            // The Globus session/consent for this identity doesn't (yet) satisfy the destination
            // collection's requirements - this is expected for brand-new or freshly re-authorized
            // identities, and surfaces asynchronously in Globus's own Activity log as a
            // "permission denied" / "500 Path not allowed" fault. Route the user through the
            // existing reauthorize flow instead of showing a raw/confusing error.
            listenerApi.dispatch(globusTransferFilesError(LS_ERROR.REAUTH_REQUIRED));
        } else {
            listenerApi.dispatch(globusTransferFilesError(
                transferResponse.message ?? 'There was an error while processing your request'))
        }
    }
})

globusListenerMiddleware.startListening({
    actionCreator: globusTransferPreviousFiles,
    effect: async (action, listenerApi) => {
        const selectedStudy = selectSelectedStudy(listenerApi.getState());
        const sourceName = action.payload.sources[action.payload.sourceIndex];
        const sourceGlobusCollection = getSourceGlobusCollection(sourceName, selectedStudy);

        const sourceCount = action.payload.sourceCounts[action.payload.sourceIndex];
        const sourceKey = action.payload.sources[action.payload.sourceIndex];

        const { data: files } = await listenerApi.dispatch(previousReleaseApiSlice.endpoints
            .previousReleaseTransfer.initiate({
                version: action.payload.fileSelection.version,
                dataset: action.payload.fileSelection.dataset,
                pipeline: action.payload.fileSelection.pipeline,
                source: sourceKey,
                page: action.payload.sourcePage,
                limit: window.env.globusLimit
            }));

        const submissionResponse = await transfer.taskSubmission.submissionId(
            {}, { manager: action.payload.authorization }
        );
        const submission = await submissionResponse.json();
        const deadline = new Date();
        deadline.setDate(deadline.getDate() + 30)
        deadline.setHours(deadline.getHours() - 1);

        const transferPayload: any = {
            submission_id: submission.value,
            source_endpoint: sourceGlobusCollection,
            destination_endpoint: action.payload.collection,
            deadline: deadline.toISOString(),
            label: `Lasso Informatics ${action.payload.fileSelection.version} Release File Transfer (${action.payload.globalPage + 1} of ${action.payload.pageCount})`,
            sync_level: 1,
            DATA: []
        }
        files.forEach((file: any) => {
            transferPayload['DATA'].push({
                DATA_TYPE:"transfer_item",
                source_path: `/${file.pathPrefix}${file.path}`,
                destination_path: `${action.payload.path}/${file.path}`
            });
        });

        const transferPromise = await transfer.taskSubmission.submitTransfer({
            payload: transferPayload
        }, { manager: action.payload.authorization });
        const transferResponse = await transferPromise.json();

        if (['Accepted', 'Duplicate'].includes(transferResponse.code)) {
            if (action.payload.globalPage === action.payload.pageCount - 1) {
                listenerApi.dispatch(globusTransferFilesComplete());
            } else if (action.payload.sourcePage === sourceCount - 1) {
                listenerApi.dispatch(globusTransferPreviousFiles({
                    ...action.payload,
                    globalPage: action.payload.globalPage + 1,
                    sourcePage: 0,
                    sourceIndex: action.payload.sourceIndex + 1
                }));
            } else {
                listenerApi.dispatch(globusTransferPreviousFiles({
                    ...action.payload,
                    globalPage: action.payload.globalPage + 1,
                    sourcePage: action.payload.sourcePage + 1
                }))
            }
        } else if (isReauthRequiredError(transferResponse)) {
            // See matching comment in the `globusTransferFiles` listener above: this is a
            // session/consent gap, not a real file-permission error, and should route the user
            // through the existing reauthorize flow rather than a raw error message.
            listenerApi.dispatch(globusTransferFilesError(LS_ERROR.REAUTH_REQUIRED));
        } else {
            listenerApi.dispatch(globusTransferFilesError(
                transferResponse.message ?? 'There was an error while processing your request'))
        }
    }
});

export { globusListenerMiddleware };